import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Hero from "../components/Hero";
import { VALUE_PROP, SERVICES, FORMATIONS, WHY_CHOOSE, COMPANY } from "../mock/mock";

const SectionTitle = ({ children, light = false }) => (
  <div className="flex flex-col items-center text-center mb-14">
    <h2
      className={`text-3xl md:text-4xl font-bold uppercase tracking-wide ${light ? "text-white" : "text-[#15324f]"}`}
      style={{ fontFamily: "'Montserrat', sans-serif" }}
    >
      {children}
    </h2>
    <div className="w-16 h-[3px] bg-[#2f80c4] mt-5" />
  </div>
);

const Home = () => {
  const featured = SERVICES.slice(0, 3);

  return (
    <div>
      <Hero />

      {/* Intro / value proposition */}
      <section className="bg-white py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <p className="text-xl md:text-2xl text-[#15324f] font-semibold leading-snug" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            {VALUE_PROP.intro}
          </p>
          <p className="mt-6 text-slate-600 text-base md:text-lg leading-relaxed">{VALUE_PROP.body}</p>
        </div>
      </section>

      {/* Services - alternating full-width image rows with offset cards */}
      <section className="bg-[#f4f6f9] pt-20 pb-4">
        <SectionTitle>Nos services</SectionTitle>
      </section>

      <div className="bg-[#f4f6f9]">
        {featured.map((s, idx) => (
          <div key={s.id} className="relative">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* image */}
              <div
                className={`relative h-[300px] lg:h-[440px] bg-cover bg-center ${idx % 2 === 1 ? "lg:order-2" : ""}`}
                style={{ backgroundImage: `url(${s.image})` }}
              >
                <div className="absolute inset-0 bg-[#0f2740]/45" />
              </div>
              {/* content */}
              <div className={`relative flex items-center ${idx % 2 === 0 ? "bg-[#1c3e5e]" : "bg-[#264a6e]"} ${idx % 2 === 1 ? "lg:order-1" : ""}`}>
                <div className="px-8 md:px-16 py-12 lg:py-0 max-w-xl">
                  <h3 className="text-white text-2xl font-bold uppercase tracking-[0.12em] mb-5" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    {s.short}
                  </h3>
                  <p className="text-white/80 leading-relaxed mb-7">{s.excerpt}</p>
                  <Link
                    to={`/services#${s.id}`}
                    className="inline-flex items-center gap-2 border-2 border-white/50 text-white px-6 py-3 text-[13px] font-semibold uppercase tracking-wide hover:bg-white hover:text-[#15324f] transition-colors duration-300"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    Plus de détails <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <section className="bg-[#f4f6f9] py-12 text-center">
        <Link
          to="/services"
          className="inline-flex items-center gap-2 bg-[#15324f] text-white px-8 py-4 text-[13px] font-semibold uppercase tracking-wide rounded hover:bg-[#2f80c4] transition-colors duration-300"
          style={{ fontFamily: "'Montserrat', sans-serif" }}
        >
          Voir tous nos services <ArrowRight size={16} />
        </Link>
      </section>

      {/* Formations */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <SectionTitle>Nos formations</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {FORMATIONS.map((f) => (
              <div key={f.id} className="group relative overflow-hidden rounded-lg shadow-md">
                <div
                  className="h-[340px] bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                  style={{ backgroundImage: `url(${f.image})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0f2740]/95 via-[#0f2740]/50 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="text-white text-2xl font-bold uppercase tracking-[0.12em] mb-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    {f.title}
                  </h3>
                  <p className="text-white/80 text-sm leading-relaxed mb-5 max-w-md">{f.excerpt}</p>
                  <Link
                    to="/formations"
                    className="inline-flex items-center gap-2 text-[#9bc3e6] hover:text-white text-[13px] font-semibold uppercase tracking-wide transition-colors"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    Plus de détails <ArrowRight size={15} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why choose us */}
      <section className="relative py-24 bg-[#15324f] overflow-hidden">
        <div className="absolute inset-0 opacity-20 bg-cover bg-center" style={{ backgroundImage: `url(${WHY_CHOOSE.image})` }} />
        <div className="absolute inset-0 bg-[#0f2740]/85" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-6">
          <SectionTitle light>Pourquoi choisir Agile Connexion ?</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {WHY_CHOOSE.points.map((p, i) => (
              <div key={p.title} className="text-center">
                <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-[#2f80c4]/20 border border-[#2f80c4]/40 flex items-center justify-center text-[#9bc3e6] text-xl font-bold" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h4 className="text-white font-semibold text-lg mb-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>{p.title}</h4>
                <p className="text-white/70 text-sm leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-14">
            <Link
              to="/a-propos"
              className="inline-flex items-center gap-2 border-2 border-white/50 text-white px-8 py-4 text-[13px] font-semibold uppercase tracking-wide hover:bg-white hover:text-[#15324f] transition-colors duration-300"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              En savoir plus <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white py-20">
        <div className="max-w-5xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#15324f] mb-5" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Prêt à transformer votre organisation ?
          </h2>
          <p className="text-slate-600 text-lg mb-8 max-w-2xl mx-auto">
            Discutons de vos enjeux en gouvernance TI et en agilité. Nos experts sont à votre écoute.
          </p>
          <Link
            to="/nous-joindre"
            className="inline-flex items-center gap-2 bg-[#2f80c4] text-white px-8 py-4 text-sm font-semibold uppercase tracking-wide rounded hover:bg-[#15324f] transition-colors duration-300"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Nous joindre <ArrowRight size={16} />
          </Link>
          <p className="mt-6 text-slate-500 text-sm">
            {COMPANY.phone} · {COMPANY.email}
          </p>
        </div>
      </section>
    </div>
  );
};

export default Home;
